/**
 * # Toast
 *
 * A component that displays a toast notification with a title, message, and dismiss button.
 * The component also exposes an `addToast({type, title, message})` method which is more commonly used. It can be seen around many queries within the application.
 * ## Props
 *
 * - `duration` (number): The duration in milliseconds for the toast to be displayed. Default is 5000.
 * - `title` (string): The title of the toast.
 * - `type` (string): The type of the toast. Can be one of 'default', 'success', 'danger', or 'warning'.
 * - `message` (string): The message content of the toast.
 * - `onDismissEnd` (function): A callback function triggered when the toast is dismissed.
 * - `aria-label` (string): The ARIA label for the toast.
 * - `permanent` (boolean): Determines if the toast is permanent and cannot be dismissed. Default is false.
 * - `shouldDismiss` (boolean): Determines if the toast should be dismissed.
 *
 * ## Usage
 *
 * ```jsx
 * import Toast from './Toast.react';
 *
 * function App() {
 *   return (
 *     <Button onClick={
 *        addToast({
 *          type: 'success',
 *          title: 'Success!',
 *          message: 'hey you made a toast'
 *       });
 *      }>
 *       Toasty
 *     </Button>
 *   );
 * }
 * ```
 */
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';

import Text from '../../atoms/Text/Text.react';
import IconButton from '../IconButton/IconButton.react';
import Card from '../../atoms/Card/Card.react';

import './toast.scss';

const backgroundColorMap = {
  default: 'blue-500',
  success: 'green-700',
  danger: 'red-500',
  warning: 'slate-600'
};

export default class Toast extends React.Component {
  static propTypes = {
    duration: PropTypes.number,
    title: PropTypes.string,
    type: PropTypes.oneOf(Object.keys(backgroundColorMap)),
    message: PropTypes.string.isRequired,
    onDismissEnd: PropTypes.func,
    'aria-label': PropTypes.string,
    permanent: PropTypes.bool,
    shouldDismiss: PropTypes.bool
  };

  static defaultProps = {
    duration: 5000,
    type: backgroundColorMap.default,
    permanent: false
  };

  constructor() {
    super();
    this.wrapperRef = React.createRef();
    this.idPrefix = uuid();

    this.state = {
      isDismissing: false,
      isDismissed: false
    };

    this.timeoutId = null;
  }

  componentDidMount() {
    const {duration, permanent} = this.props;

    if (!permanent && duration) {
      this.timeoutId = setTimeout(this.handleDismiss, duration);
    }

    this.wrapperRef.current.addEventListener('transitionend', this.handleDismissEnd);
    this.wrapperRef.current.addEventListener('animationend', this.handleDismissEnd);
  }

  componentDidUpdate() {
    if (this.props.shouldDismiss) {
      this.handleDismiss();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutId);
    this.wrapperRef.current?.removeEventListener('transitionend', this.handleDismissEnd);
    this.wrapperRef.current?.removeEventListener('animationend', this.handleDismissEnd);
  }

  handleDismiss = () => {
    if (this.state.isDismissing) {
      return;
    }
    this.setState({isDismissing: true});
  };

  handleDismissEnd = (e) => {
    if (!this.state.isDismissing || e.target !== e.currentTarget) {
      return;
    }

    this.setState({isDismissed: true});
    if (this.props.onDismissEnd) {
      this.props.onDismissEnd();
    }
  };

  render() {
    if (this.state.isDismissed) {
      return null;
    }
    const {title, message, type, 'aria-label': ariaLabel} = this.props;
    const ariaWrapperProps = {};
    const titleId = `${this.idPrefix}label`;
    const descriptionId = `${this.idPrefix}description`;
    if (ariaLabel) {
      ariaWrapperProps['aria-label'] = ariaLabel;
    } else if (title) {
      ariaWrapperProps['aria-labelledBy'] = titleId;
    } else {
      // eslint-disable-next-line no-console
      console.warn('Toast must be given a title or an `aria-label` prop');
    }

    return (
      <Card
        border='none'
        ref={this.wrapperRef}
        backgroundColor={backgroundColorMap[type]}
        className={classnames('m-toast u-display_inline-flex', {
          'm-toast--dismissing': this.state.isDismissing
        })}
        data-testid='toast'
        role={this.props.type === 'danger' ? 'alert' : 'status'}
        {...ariaWrapperProps}
      >
        <div className='m-toast__content-wrapper'>
          {title && (
            <Text bold color={null} as='p' className='m-toast__title' id={titleId}>
              {title}
            </Text>
          )}
          <Text
            paragraph
            as='p'
            size='m'
            color={null}
            withMargin={false}
            className='m-toast__description'
            id={descriptionId}
          >
            {message}
          </Text>
        </div>
        <div className='m-toast__dismiss-btn-wrapper'>
          <IconButton
            className='m-toast__dismiss-btn u-mar-l_2 u-color_white'
            disabled={this.state.isDismissing}
            icon='times'
            label='Dismiss banner'
            onClick={this.handleDismiss}
          />
        </div>
      </Card>
    );
  }
}
